// Globals
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  createTopProduct,
  getProductsList,
  getTopProductList,
  productsSelector,
  updateTopProduct,
} from '../../feature';

// Components
import { TopProductForm } from '../../components';
import { IFormInstance, showSuccessToast } from '@/components';

// Models
import { ITopProduct } from '@/models/products.model';

// Helpers
import { TGetTopProductsForm, TopProductsFields } from '../../helpers';

type TAddTopProductContainer = {
  currentProduct: ITopProduct | null;
  formInstance: IFormInstance;
  handleCloseDrawer: () => void;
  isLoading: boolean;
};

const AddTopProductContainer = ({
  formInstance,
  handleCloseDrawer,
  isLoading,
  currentProduct,
}: TAddTopProductContainer) => {
  const { t } = useTranslation(['products']);
  const dispatch = useAppDispatch();
  const { productsList, productsOfMonthQuery } = useAppState(productsSelector);
  const isEdit = !!currentProduct?.id;

  const handleSubmit = useCallback(
    async (values: TGetTopProductsForm) => {
      if (isEdit) {
        await dispatch(
          updateTopProduct({
            id: currentProduct?.id,
            params: { product_id: values[TopProductsFields.PRODUCT_ID] },
          }),
        ).unwrap();
      } else {
        await dispatch(createTopProduct({ product_id: values[TopProductsFields.PRODUCT_ID] })).unwrap();
      }

      await dispatch(getTopProductList()).unwrap();

      handleCloseDrawer();
      showSuccessToast({ message: t(isEdit ? 'data_saved' : 'top_product_saved_weekly') });
    },
    [handleCloseDrawer, isEdit, currentProduct, productsOfMonthQuery],
  );

  const debouncedFetchProducts = useCallback(
    debounce((searchText: string) => {
      dispatch(getProductsList({ sku: searchText }));
    }, 1000),
    [],
  );

  useEffect(() => {
    dispatch(getProductsList({ name: '' }));
  }, []);

  return (
    <TopProductForm
      formInstance={formInstance}
      initialValues={currentProduct}
      productsList={productsList?.data}
      handleSubmit={handleSubmit}
      isEdit={isEdit}
      isLoading={isLoading}
      debouncedFetchProducts={debouncedFetchProducts}
    />
  );
};

export default AddTopProductContainer;
